// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef, useCallback, useLayoutEffect } from 'react';

export type MutableSizeArgs = {
  defaultSize: number;
  min: number;
  max: number;
  type?: 'width' | 'height';
};

/**
 * Uses a ref to update the width of a given element. Useful for when size changes are too frequent for react's state management,
 * such as when using DraggableDivider
 * @param defaultSize The default size of the element
 * @param min The minimum size of the element
 * @param max The maximum size of the element
 * @param type The dimension to update. Defaults to 'width'.
 */
export const useMutableSize = <T extends HTMLElement>({ defaultSize, min, max, type = 'width' }: MutableSizeArgs) => {
  const ref = useRef<T>(null);
  const setSize = useCallback(
    (sizeOrSizeFn: number | ((width: number) => number)) => {
      if (ref.current) {
        const newSize =
          typeof sizeOrSizeFn === 'function'
            ? sizeOrSizeFn(Number(ref.current[type === 'width' ? 'clientWidth' : 'clientHeight']))
            : sizeOrSizeFn;
        const clampedSize = Math.min(max, Math.max(min, newSize));
        ref.current.style[type] = `${clampedSize}px`;
      }
    },
    [type, min, max],
  );
  useLayoutEffect(() => setSize(defaultSize), [defaultSize, setSize]);
  return [ref, setSize] as const;
};
